import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	useUpdateDepartmentMutation,
	useCreateDepartmentMutation,
	Department,
	Contact,
	useGetAllMembersOfLazyQuery,
} from "@repo/server-api";
import { OptionType } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import {
	emptyContact,
	setContactValues,
	validErrorState,
} from "@repo/legacy/components/helper/Index";

import Dropdown from "../sections/Dropdown";

export default function DialogDepartment(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		departmant: Department | null;
		contactId: string;
	}>,
) {
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [departmant, setDepartment] = useState<Department>({
		companyId: "",
		description: "",
		id: "",
		leader: {
			...emptyContact,
		},
		leaderId: "",
		name: "",
		version: 0,
	});
	const [employees, setEmployees] = useState<Contact[]>([]);
	const [optionElement, setOptionElement] = useState<OptionType | null>(null);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [updateDepartment] = useUpdateDepartmentMutation({
		variables: {
			name: departmant.name,
			description: departmant.description,
			leaderId: departmant.leaderId ?? "",
			id: departmant.id,
		},
		onCompleted(data) {
			if (data.updateDepartment.department) {
				props.refreshData();
				handleClose();
			} else if (data.updateDepartment.errors) {
				const errors = data.updateDepartment.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [createDepartment] = useCreateDepartmentMutation({
		variables: {
			name: departmant.name,
			description: departmant.description,
			leaderId: departmant.leaderId ?? "",
			companyId: props.contactId,
		},
		onCompleted(data) {
			if (data.createDepartment.department) {
				props.refreshData();
				handleClose();
			} else if (data.createDepartment.errors) {
				const errors = data.createDepartment.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [getEmployees] = useGetAllMembersOfLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allMembersOf) {
				setEmployees(data.allMembersOf.nodes as unknown as Contact[]);
			}
		},
	});

	useEffect(() => {
		setOpen(props.open);
		if (props.departmant) {
			setDepartment({
				...props.departmant,
			});
			setOptionElement({
				title: props.departmant.leader?.displayName ?? "",
				inputValue: props.departmant.leader?.displayName ?? "",
				item: {
					...setContactValues(props.departmant.leader ?? emptyContact),
					id: props.departmant.leaderId ?? "",
					displayName: props.departmant.leader?.displayName ?? "",
				},
			});
		} else {
			setDepartment({
				companyId: "",
				description: "",
				id: "",
				leader: {
					...emptyContact,
				},
				leaderId: "",
				name: "",
				version: 0,
			});
			setOptionElement({
				title: "",
				inputValue: "",
				item: null,
			});
		}
		setErrors({});
		getEmployees({
			variables: {
				contactId: props.contactId,
				first: 100,
				after: null,
			},
		});
	}, [props.open]);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
		description: Yup.string().required(t("validation.description.required")),
		leaderId: Yup.string().required(t("validation.leaderId.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(departmant, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		setOpen(false);
		setErrors({});
		setDepartment({
			companyId: "",
			description: "",
			id: "",
			leader: {
				...emptyContact,
			},
			leaderId: "",
			name: "",
			version: 0,
		});
		setOptionElement({
			title: "",
			inputValue: "",
			item: null,
		});
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (isValid) {
			setSaveClicked(true);
			if (departmant.id) {
				updateDepartment();
			} else {
				createDepartment();
			}
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true}>
			<DialogTitle>
				{departmant?.id
					? t("contractors.department.dialog.headerEdit")
					: t("contractors.department.dialog.headerNew")}
			</DialogTitle>
			<DialogContent className="flex-col gap-6">
				<Box className="fullCenter">
					<TextField
						onChange={(e) => {
							setDepartment({
								...departmant,
								name: e.target.value ?? "",
							});
							setErrors({ ...errors, name: "" });
						}}
						error={errors.name as boolean}
						helperText={errors.name as string}
						className="w-100"
						value={departmant.name ?? ""}
						label={t("name")}
						variant="outlined"
					/>
				</Box>
				<Box className="fullCenter">
					<TextField
						onChange={(e) => {
							setDepartment({
								...departmant,
								description: e.target.value ?? "",
							});
							setErrors({ ...errors, description: "" });
						}}
						error={errors.description as boolean}
						helperText={errors.description as string}
						className="w-100"
						value={departmant.description ?? ""}
						label={t("no-description")}
						variant="outlined"
						rows={4}
						multiline
					/>
				</Box>
				<Box className="fullCenter">
					<Dropdown
						doNotShowMenu={true}
						element={{
							title: optionElement?.title ?? "",
							inputValue: optionElement?.inputValue ?? "",
							item: optionElement?.item ?? null,
						}}
						setElement={(o) => {
							setOptionElement(o);
							if (o?.item) {
								setDepartment({
									...departmant,
									leaderId: o.item.id,
								});
							} else {
								setDepartment({
									...departmant,
									leaderId: "",
								});
							}
						}}
						elements={employees}
						error={errors?.leaderId as string}
						setError={(value) => setErrors?.({ ...errors, leaderId: value })}
						searchPlaceholder={t("dropdown.placeholderEmployee")}
						searchLabel={t("dropdown.leaderSearch")}
						noData={t("dropdown.noEmployees")}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					variant="contained"
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
